/**
 * M10.8: "Új verzió érkezett". The server announces a finished deployment over the WebSocket
 * (`app.version`) and /api/public/version is polled as a fallback (a missed push, a restarted API).
 *
 *  - newer version, compatible client  -> a quiet banner: open the changelog, reload when ready
 *  - the client is older than the version's minClientVersion -> the update is required: a blocking
 *    dialog, but never while a live battle is running (reloading would drop the player out of it)
 *  - reload is always the player's click: we never reload by ourselves
 */
import { useCallback, useEffect, useState } from 'react';
import { useGameStore } from '../store/game-store.ts';
import { CLIENT_VERSION, compareVersions, fetchPublicVersion } from '../api/updates.ts';
import { ChangelogLink } from './ChangelogPage.tsx';

interface Announced { version: string; minClientVersion: string | null }
const POLL_MS = 90_000;

export function UpdateBanner() {
  const [latest, setLatest] = useState<Announced | null>(null);
  const liveBattle = useGameStore(s => !!s.liveBattle && !s.liveBattle.isComplete);

  const consider = useCallback((a: Announced) => {
    if (CLIENT_VERSION === 'dev') return; // local development builds have no release
    if (compareVersions(a.version, CLIENT_VERSION) > 0) setLatest(a);
  }, []);

  useEffect(() => {
    const onPush = (e: Event) => consider((e as CustomEvent<Announced>).detail);
    window.addEventListener('galaxis:app-version', onPush);
    const poll = () => { fetchPublicVersion().then(consider).catch(() => undefined); };
    poll();
    const t = setInterval(poll, POLL_MS);
    // coming back to the tab is the natural moment to look again
    const onBack = () => { if (document.visibilityState === 'visible') poll(); };
    window.addEventListener('focus', onBack);
    document.addEventListener('visibilitychange', onBack);
    return () => { window.removeEventListener('galaxis:app-version', onPush); window.removeEventListener('focus', onBack); document.removeEventListener('visibilitychange', onBack); clearInterval(t); };
  }, [consider]);

  if (!latest) return null;
  const required = !!latest.minClientVersion && compareVersions(CLIENT_VERSION, latest.minClientVersion) < 0;
  const reload = () => window.location.reload();

  if (required && !liveBattle) {
    return (
      <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" role="alertdialog" aria-modal="true" aria-labelledby="upd-req-title" data-testid="update-required">
        <div className="sci-fi-panel rounded-xl p-5 w-full max-w-sm flex flex-col gap-3">
          <h2 id="upd-req-title" className="text-base font-semibold text-slate-100">Frissítés szükséges</h2>
          <p className="text-sm text-slate-300">A játék új, nem kompatibilis verziója ({latest.version}) érkezett. A folytatáshoz töltsd újra az oldalt.</p>
          <ChangelogLink label="Mi változott?" testId="update-changelog" className="btn-link self-start" />
          <button type="button" className="btn-primary min-h-[44px]" onClick={reload} data-testid="update-reload">Újratöltés</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed left-[calc(0.5rem+var(--sal))] right-[calc(0.5rem+var(--sar))] bottom-[calc(0.5rem+var(--sab))] sm:left-auto sm:right-[calc(1rem+var(--sar))] sm:bottom-[calc(1rem+var(--sab))] sm:w-96 z-[60] sci-fi-panel rounded-xl p-3 flex flex-col gap-2 shadow-lg"
      role="status" aria-live="polite" data-testid="update-banner">
      <div className="flex items-start gap-2">
        <span aria-hidden className="text-lg leading-none">✨</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-100">Új verzió érkezett: <span className="font-mono">{latest.version}</span></p>
          {liveBattle
            ? <p className="text-xs text-amber-300 mt-0.5" data-testid="update-deferred">Csata közben nem frissítünk. A harc után újratöltheted az oldalt.{required ? ' A frissítés kötelező lesz.' : ''}</p>
            : <p className="text-xs text-slate-400 mt-0.5">Töltsd újra az oldalt, amikor megfelel.</p>}
        </div>
        <button type="button" className="btn-link min-h-[44px] min-w-[44px]" aria-label="Értesítés elrejtése" onClick={() => setLatest(null)}>✕</button>
      </div>
      <div className="flex gap-2">
        <ChangelogLink label="Újdonságok" testId="update-changelog" className="btn-secondary flex-1 justify-center min-h-[44px] inline-flex items-center" />
        <button type="button" className="btn-primary flex-1 min-h-[44px]" disabled={liveBattle} onClick={reload} data-testid="update-reload">Újratöltés</button>
      </div>
    </div>
  );
}
